package org.matthewo.schema.games

import org.matthewo.api.MatchOutcome
import org.matthewo.api.MetricsIntoTheDeepCRIAPI
import org.matthewo.math.div
import org.matthewo.math.minus
import org.matthewo.math.plus
import org.matthewo.schema.CRI_YEAR_PREFIX
import org.matthewo.schema.RankingDetails
import org.matthewo.schema.RankingMetrics
import org.matthewo.schema.StandardMetrics
import org.matthewo.symbolprocessor.GameSchema
import org.matthewo.symbolprocessor.Metric
import org.matthewo.symbolprocessor.Period
import kotlin.math.E
import kotlin.math.abs
import kotlin.math.pow

@GameSchema(CRI_YEAR_PREFIX + 2024, "Into The Deep CRI", 3)
interface MetricsIntoTheDeepCRI<T : Number> : StandardMetrics<T>, RankingMetrics<T> {
    override val tbp1 get() = (apSampleNet + apSampleLow + apSampleHigh + apSpecimenLow + apSpecimenHigh) / 2 + apNavigation

    @Metric(Period.ALL, "Foul")
    override val penalty: T

    @Metric(Period.AUTO, "Sample Net")
    val apSampleNet get() = apSampleNetNear + apSampleNetFar

    @Metric(Period.AUTO, "Sample Low")
    val apSampleLow get() = apSampleLowNear + apSampleLowFar

    @Metric(Period.AUTO, "Sample High")
    val apSampleHigh get() = apSampleHighNear + apSampleHighFar

    @Metric(Period.AUTO, "Specimen Low")
    val apSpecimenLow get() = apSpecimenLow1 + apSpecimenLow2 + apSpecimenLow3 + apSpecimenLow4

    @Metric(Period.AUTO, "Specimen High")
    val apSpecimenHigh get() = apSpecimenHigh1 + apSpecimenHigh2 + apSpecimenHigh3 + apSpecimenHigh4

    @Metric(Period.AUTO, "Ownership")
    val apOwned: T

    @Metric(Period.AUTO, "Navigation")
    val apNavigation: T


    @Metric(Period.TELEOP, "Sample Net")
    val tpSampleNet get() = tpSampleNetNear + tpSampleNetFar

    @Metric(Period.TELEOP, "Sample Low")
    val tpSampleLow get() = tpSampleLowNear + tpSampleLowFar

    @Metric(Period.TELEOP, "Sample High")
    val tpSampleHigh get() = tpSampleHighNear + tpSampleHighFar

    @Metric(Period.TELEOP, "Specimen Low")
    val tpSpecimenLow get() = tpSpecimenLow1 + tpSpecimenLow2 + tpSpecimenLow3 + tpSpecimenLow4

    @Metric(Period.TELEOP, "Specimen High")
    val tpSpecimenHigh get() = tpSpecimenHigh1 + tpSpecimenHigh2 + tpSpecimenHigh3 + tpSpecimenHigh4


    @Metric(Period.TELEOP, "Ownership")
    val tpOwned: T

    @Metric(Period.ENDGAME, "Navigation")
    val epNavigation: T

    @Metric(Period.ENDGAME, "Coopertition")
    val epCoopertition: T


    val apSampleNetNear: T
    val apSampleLowNear: T
    val apSampleHighNear: T
    val apSampleNetFar: T
    val apSampleLowFar: T
    val apSampleHighFar: T
    val apSpecimenLow1: T
    val apSpecimenHigh1: T
    val apSpecimenLow2: T
    val apSpecimenHigh2: T
    val apSpecimenLow3: T
    val apSpecimenHigh3: T
    val apSpecimenLow4: T
    val apSpecimenHigh4: T

    val tpSampleNetNear: T
    val tpSampleLowNear: T
    val tpSampleHighNear: T
    val tpSampleNetFar: T
    val tpSampleLowFar: T
    val tpSampleHighFar: T
    val tpSpecimenLow1: T
    val tpSpecimenHigh1: T
    val tpSpecimenLow2: T
    val tpSpecimenHigh2: T
    val tpSpecimenLow3: T
    val tpSpecimenHigh3: T
    val tpSpecimenLow4: T
    val tpSpecimenHigh4: T

    companion object {
        const val COOPERTITION = 50
        const val AUTO_OWNED = 10
        const val TELEOP_OWNED = 20
    }
}

val <T : Number> MetricsIntoTheDeepCRI<T>.sample: Double
    get() = apSampleNet.toDouble() + apSampleLow.toDouble() + apSampleHigh.toDouble() +
            tpSampleNet.toDouble() + tpSampleLow.toDouble() + tpSampleHigh.toDouble()

val <T : Number> MetricsIntoTheDeepCRI<T>.specimen: Double
    get() = apSpecimenLow.toDouble() + apSpecimenHigh.toDouble() +
            tpSpecimenLow.toDouble() + tpSpecimenHigh.toDouble() +
            apOwned.toDouble() + tpOwned.toDouble()

val <T : Number> MetricsIntoTheDeepCRI<T>.navigation: Double
    get() = apNavigation.toDouble() + epNavigation.toDouble()

fun <T : Number> MutableMetricsIntoTheDeepCRI<T>.computeTotals() {
    auto = apSampleNet + apSampleLow + apSampleHigh + apSpecimenLow + apSpecimenHigh + apOwned + apNavigation
    teleop = tpSampleNet + tpSampleLow + tpSampleHigh + tpSpecimenLow + tpSpecimenHigh + tpOwned
    endgame = epCoopertition + epNavigation
    npTotal = auto + teleop + endgame
    total = npTotal - penalty
}

object IntoTheDeepRankingDetailsCRI : RankingDetails<MetricsIntoTheDeepCRI<*>> {
    override fun combine(team1: MetricsIntoTheDeepCRI<*>, team2: MetricsIntoTheDeepCRI<*>) = MetricsIntoTheDeepCRIAPI<Double>(team1, team2)

    override fun winProbability(alliance: MetricsIntoTheDeepCRI<*>?, opponent: MetricsIntoTheDeepCRI<*>?, playedFactor: Double): Double {
        if (null == alliance || null == opponent) return 0.5
        val diff = alliance.npTotal.toDouble() - opponent.npTotal.toDouble()
        val percent = -0.5 * E.pow(-0.025 * abs(diff)) + 1.0
        return if (diff >= 0) percent else 1.0 - percent
    }

    override fun rp(outcome: MatchOutcome, score: MetricsIntoTheDeepCRI<*>?, project: Boolean): Number {
        return 0 // TODO: need a way to handle event level stats used in rankings
    }

    override fun tbp1(score: MetricsIntoTheDeepCRI<*>): Number {
        return score.tbp1
    }
}
